'use client';
import type { HeaderFeatureProps as Props } from './types';

import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { Button, Title, Flex } from '@gjensidige/builders-components';
import { Play, Pause } from '@gjensidige/builders-icons';

import styles from './header-feature.module.less';

const SIZES = `(min-width: 1024px) and (max-width: 1199px) 1024px, (min-width: 1200px) and (max-width: 1619px) 1200px, (min-width: 1620px) 2200px, 100vw`;

export const HeaderFeature = ({
  title,
  image,
  buttons,
  mobileImage,
  video,
  mobileVideo
}: Props) => {
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const [pausedVideo, setPausedVideo] = useState(false);
  const [showVideo, setShowVideo] = useState(Boolean(video));
  const [showMobileVideo, setShowMobileVideo] = useState(Boolean(mobileVideo));

  const togglePlayPause = (
    video: HTMLVideoElement | null,
    forcePause?: boolean
  ) => {
    if (!video) return;
    if (video.paused && !forcePause) video.play();
    else video.pause();
    setPausedVideo(video.paused);
  };

  useEffect(() => {
    if (!video && !mobileVideo) return; // return if no video to avoid vitest to fail from following code
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) {
      setShowVideo(false);
      setShowMobileVideo(false);
    } else {
      setShowVideo(true);
      setShowMobileVideo(true);
    }
  }, [video, mobileVideo]);

  return (
    <Flex
      align="end"
      as="header"
      className={classNames(styles.headerFeature, styles.marginBHeaders)}
    >
      {mobileVideo && showMobileVideo ? (
        <video
          loop
          autoPlay
          muted
          preload="metadata"
          playsInline
          ref={mobileVideoRef}
          aria-hidden="true"
          className={styles.videoMobile}
        >
          <source src={mobileVideo} type="video/mp4" />
        </video>
      ) : (
        <img
          alt={mobileImage?.alt || image?.alt || ''}
          sizes={SIZES}
          src={mobileImage?.url || image?.url}
          srcSet={mobileImage?.srcset || image?.srcset}
        />
      )}
      {video && showVideo ? (
        <video
          loop
          autoPlay
          muted
          ref={desktopVideoRef}
          aria-hidden="true"
          className={styles.videoDesktop}
          playsInline
          preload="metadata"
        >
          <source src={video} type="video/mp4" />
        </video>
      ) : (
        <img
          alt={image?.alt || ''}
          hidden
          sizes={SIZES}
          src={image?.url}
          srcSet={image?.srcset}
        />
      )}
      <Flex center layout="1">
        <Flex layout={{ xs: 1, md: 8.4, xl: 7.5 }} gap={{ xs: 'xs', md: 'md' }}>
          {!!title && <Title as="h1" size="2" text={title} uppercase />}
        </Flex>
        <Flex gap={{ xs: 'xs sm', md: 'md' }}>
          {buttons?.map(({ text, url }, index, { length }) => (
            <Button
              key={index}
              href={url}
              data-theme="dark"
              variant={
                length === 1 ? 'critical' : index ? 'secondary' : 'primary'
              }
            >
              {text}
            </Button>
          ))}
          {(showVideo || showMobileVideo) && (
            <Button
              icon={pausedVideo ? <Play /> : <Pause />}
              onClick={() => {
                mobileVideoRef.current?.offsetWidth &&
                  togglePlayPause(mobileVideoRef.current);
                desktopVideoRef.current?.offsetWidth &&
                  togglePlayPause(desktopVideoRef.current);
              }}
              data-mobile-video={!!mobileVideo}
              data-desktop-video={!!video}
              className={styles.playPause}
              aria-hidden="true"
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
